import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { IUserData } from 'src/api/user';
import { AngleIcon, ExternalLinkIcon } from '../../../../components/Icons';
import { Heading } from '../Heading';
import { Privacy } from '../Privacy';
import { RemoveTwin } from '../RemoveTwin';
import { ShareButtons } from '../ShareButtons';

export interface TwinReadyContentProps {
  user: IUserData;
  locationSearch: string | null;
  twinImageUrl: string;
  refetchUser: () => void;
}

const TwinReadyContent: React.FC<TwinReadyContentProps> = memo(
  ({ user, locationSearch, twinImageUrl, refetchUser }) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const isMacos = searchParams.get('platform') === 'macos';
    const location = useLocation();

    return (
      <>
        <img
          className="w-24 h-24 rounded-full object-cover mb-2"
          src={twinImageUrl}
          alt="Twin"
        />

        <Heading title={t('twin.readyTitle')} />
        <div className="my-0 sm:my-14 flex gap-4 flex-col sm:flex-row w-full justify-center">
          <Menu>
            <MenuButton className="btn btn-primary focus:outline-none block">
              {t('twin.editYourTwin')}
              <div className="-rotate-90 ml-2">
                <AngleIcon />
              </div>
            </MenuButton>
            <MenuItems
              modal={false}
              portal={true}
              anchor="bottom"
              className="[--anchor-gap:8px] w-(--button-width) shadow-lg z-[99999] tc-mv-blur rounded-2xl p-2 focus:outline-none origin-top transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
              transition>
              <MenuItem
                as={Link}
                to="/twin/knowledge/files"
                className="w-full px-4 py-2 rounded-lg text-sm text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center transition-colors duration-150">
                {t('twin.knowledgeBase')}
              </MenuItem>
              <MenuItem
                as={Link}
                to="/twin/knowledge/faq"
                className="w-full px-4 py-2 rounded-lg text-sm text-gray-200 hover:bg-black hover:bg-opacity-20 flex items-center transition-colors duration-150">
                {t('twin.faq')}
              </MenuItem>
            </MenuItems>
          </Menu>

          <Link
            data-testid="talk-with-twin-link"
            to={
              isMacos
                ? `napster-companion://companion/start/${user?.mentorId}`
                : `/${user.mentorSlug}${
                    locationSearch
                      ? `${locationSearch}&start=true`
                      : '?start=true'
                  }`
            }
            state={{
              prevLocation: `${location.pathname}${location.search}${location.hash}`,
            }}
            className="btn btn-primary block">
            {t('twin.talkWithTwin')}&nbsp;
            <ExternalLinkIcon height={16} width={16} />
          </Link>
        </div>

        <h3 className="text-xl mt-4 sm:mt-8">{t('twin.shareYourTwin')}</h3>
        <p className="text-center max-w-[600px] mx-auto">
          {t('twin.shareDescription')}
        </p>

        <div className="mt-4">
          <ShareButtons slug={user.mentorSlug} />
        </div>
        <div className="mb-auto sm:mb-0">
          <RemoveTwin refetchUser={refetchUser} />
        </div>
        <Privacy />
      </>
    );
  },
);

export { TwinReadyContent };
